import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import GetIcon from '../../utils/Icon';

const Table = ({
  columns,
  data = [],
  loading,
  onRowClick,
  className = '',
  headerClassName = '',
  rowClassName = '',
  emptyMessage = 'No data available',
  loadingMessage = 'Loading...'
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const getValue = (obj) => {
        return sortConfig.key.split('.').reduce((o, i) => o?.[i], obj);
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue === bValue) return 0;

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  const handleSort = (key, isSortable) => {
    if (!isSortable) return;

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
  };

  return (
    <div
      className={`p-2 ${className}`}
      style={{ 
        overflowX: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: '#fc8b5c transparent',
      }}
    >
      <style>
        {`
          ::-webkit-scrollbar {
            height: 8px;
            width: 8px;
          }
          ::-webkit-scrollbar-track {
            background: transparent;
          }
          ::-webkit-scrollbar-thumb {
            background-color: #fc8b5c;
            border-radius: 10px;
          }
        `}
      </style>
      <table
        className="table"
        style={{ minWidth: '1300px', width: '100%' }}
      >
        <thead className={`thead-dark ${headerClassName}`}>
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className={`table-header ${column.header === 'Action' ? 'text-center' : 'text-start'} ${column.sortable ? 'sortable' : ''} ${sortConfig.key === (column.accessor || column.header) ? 'active-sort' : ''}`}
                style={{ width: column.width || 'auto', whiteSpace: 'nowrap' }}
                onClick={() => handleSort(column.accessor || column.header, column.sortable)}
              >
                <div>
                  {column.header}
                  {column.sortable && (
                    <span className="sort-icon ms-1">
                      {sortConfig.key === (column.accessor || column.header) ? (
                        <span style={{ color: '#391F84' }}>
                          {sortConfig.direction === 'asc'
                            ? <GetIcon iconName="FaSortUp" size={16} design="upsort" />
                            : <GetIcon iconName="FaSortDown" size={16} design="downsort" />
                          }
                        </span>
                      ) : (
                        <span style={{ opacity: 0.5 }}>↕</span>
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="text-center">
                {loadingMessage}
              </td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                className={rowClassName}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...(row.style || {})
                }}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={column.header === 'Action' ? 'text-center' : 'text-start'}
                    style={{
                      width: column.width || 'auto',
                      whiteSpace: 'nowrap',
                      ...column.style,
                    }}
                  >
                    {column.cellRenderer
                      ? column.cellRenderer(row, rowIndex, column)
                      : row[column.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.string,
      cellRenderer: PropTypes.func,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      style: PropTypes.object,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  rowClassName: PropTypes.string,
  emptyMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
};

Table.defaultProps = {
  loading: false,
};

export default Table;